import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'

import { ConfirmDialog } from '@/components/feedback/ConfirmDialog'
import { Empty } from '@/components/feedback/Empty'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { CreateGroupDialog } from '@/features/admin/CreateGroupDialog'
import { EditGroupDialog } from '@/features/admin/EditGroupDialog'
import { GroupPermissionMatrix } from '@/features/admin/GroupPermissionMatrix'
import {
  SCOPE_DOMAINS,
  canonicalPermissions,
  canonicalSelection,
  changedAccess,
  groupAccessValues,
  type GroupAccessValues,
} from '@/features/admin/group-form'
import { AREA_ORDER, permissionsByArea, selectedCount, type AreaKey } from '@/lib/group-areas'
import {
  EMPTY_SELECTION,
  firmState,
  hasBranch,
  isUnrestricted,
  selectionLabels,
  toggleBranch,
  toggleFirm,
  type FirmBranchSelection,
} from '@/lib/firm-branch-scope'
import { useQueryState } from '@/hooks/use-query-state'
import { useRecordEditor } from '@/lib/form/use-record-editor'
import { useRecordVersion } from '@/lib/form/use-record-version'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import { services } from '@/services'
import { cn } from '@/lib/utils'
import type { Group, Permission, ScopeDomain } from '@/types/models'

/** Kirli ayrıntı formunun "kapat/başka yere git" koruması (`useRecordEditor().requestClose`). */
type LeaveGuard = (action: () => void) => void

function PermissionChecklist({
  permissions,
  selected,
  onToggle,
  byGroup = false,
}: {
  permissions: Permission[]
  selected: string[]
  onToggle: (key: string) => void
  byGroup?: boolean
}) {
  const sections = new Map<string, Permission[]>()
  for (const permission of permissions) {
    const name = byGroup ? permission.group : ''
    sections.set(name, [...(sections.get(name) ?? []), permission])
  }
  return (
    <div className="flex min-w-0 flex-col gap-3">
      {[...sections.entries()].map(([groupName, items]) => (
        <div key={groupName} className="flex flex-col gap-1">
          {groupName && (
            <span className="text-muted-foreground text-xs font-medium uppercase">{groupName}</span>
          )}
          {items.map((permission) => (
            <label
              key={permission.key}
              className="hover:bg-accent flex min-w-0 items-center gap-2 rounded-sm px-1 py-1 text-sm"
            >
              <Checkbox
                checked={selected.includes(permission.key)}
                onCheckedChange={() => onToggle(permission.key)}
              />
              <span className="min-w-0">{permission.label}</span>
              <code className="text-muted-foreground ml-auto hidden shrink-0 text-xs sm:inline">
                {permission.key}
              </code>
            </label>
          ))}
        </div>
      ))}
    </div>
  )
}

function ScopeHeading({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="flex flex-col gap-1">
      <h4 className="text-sm font-medium">{title}</h4>
      <p className="text-muted-foreground text-xs">
        {hint} {messages.groups.scopeRule}
      </p>
    </div>
  )
}

/**
 * Alanın firma/şube kısıtı: firma kutusu firmanın tüm şubelerini açar, şube kutuları yalnız o
 * şubeyi (Kantar'da şube = kantar). Boş seçim = kısıt yok (bkz. backend
 * `groups.service.firm_scope`). Şubesiz alanda (Finans) yalnız firma kutuları çıkar.
 */
function FirmScopeEditor({
  domain,
  selection,
  onChange,
}: {
  domain: ScopeDomain
  selection: FirmBranchSelection
  onChange: (update: (current: FirmBranchSelection) => FirmBranchSelection) => void
}) {
  const optionsQuery = useQuery({
    queryKey: queryKeys.admin.firmBranchOptions(domain),
    queryFn: () => services.admin.listFirmBranchOptions(domain),
  })
  const text = messages.groups
  const allBranches = domain === 'finans' ? null : text.firmAllBranches[domain]

  if (optionsQuery.isLoading) return <Loading rows={3} />
  if (optionsQuery.isError)
    return <ErrorState error={optionsQuery.error} onRetry={() => void optionsQuery.refetch()} />

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <ScopeHeading title={text.areas[domain].scope} hint={text.areas[domain].hint} />
      <label className="flex items-center gap-2 py-1 text-sm font-medium">
        <Checkbox
          checked={isUnrestricted(selection)}
          onCheckedChange={() => onChange(() => EMPTY_SELECTION)}
        />
        <span>{text.firmAll}</span>
      </label>
      <div className="flex flex-col gap-2">
        {(optionsQuery.data ?? []).map((option) => {
          const state = firmState(selection, option.firma)
          return (
            <div key={option.firma} className="flex flex-col gap-1">
              <label className="flex items-center gap-2 py-1 text-sm">
                <Checkbox
                  checked={state === 'all'}
                  indeterminate={state === 'some'}
                  onCheckedChange={() => onChange((current) => toggleFirm(current, option.firma))}
                />
                <span className="font-medium">{option.firma}</span>
                {allBranches && (
                  <span className="text-muted-foreground text-xs">({allBranches})</span>
                )}
              </label>
              {allBranches &&
                (option.subeler.length > 0 ? (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 pl-7">
                    {option.subeler.map((sube) => (
                      <label key={sube} className="flex items-center gap-2 py-0.5 text-sm">
                        <Checkbox
                          checked={state === 'all' || hasBranch(selection, option.firma, sube)}
                          disabled={state === 'all'}
                          onCheckedChange={() =>
                            onChange((current) => toggleBranch(current, option.firma, sube))
                          }
                        />
                        <span>{sube}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground pl-7 text-xs">{text.firmNoBranches}</p>
                ))}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function AreaBlock({
  title,
  summary,
  children,
}: {
  title: string
  summary: string
  children: React.ReactNode
}) {
  return (
    <section className="border-border rounded-md border" aria-label={title}>
      <header className="border-border flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b px-3 py-2">
        <h3 className="text-sm font-medium">{title}</h3>
        <span className="text-muted-foreground text-xs">{summary}</span>
      </header>
      <div className="grid gap-4 p-3 xl:grid-cols-2">{children}</div>
    </section>
  )
}

/**
 * Grubun izinleri ve kapsamları alan alan (GRUP-KAPSAM-PLAN Faz 3): İK, Finans & Banka, Kantar
 * bloklarında o alanın izinleri ile firma/şube (kantar) seçimi yan yana; kapsamı olmayan izinler
 * "Diğer"de. Tek "Kaydet" yalnız değişen parçaları gönderir.
 */
function GroupDetail({
  group,
  registerGuard,
}: {
  group: Group
  registerGuard: (guard: LeaveGuard | null) => void
}) {
  const queryClient = useQueryClient()
  const permissionsQuery = useQuery({
    queryKey: queryKeys.admin.permissions(),
    queryFn: () => services.admin.listPermissions(),
  })
  // `base`: formun başladığı grup (sürüm kapısı için "açıldığı" hâl). Form temizken başkasının daha yeni
  // değişikliğini izler; kirliyken arka plan tazelemesi ne formu ne sürümü ilerletir, yazma eski sürümle
  // gider ve 409 alır. Kendi yazmalarımız `base`i yanıttaki gruba ilerletir. `baseRef` bölünmüş
  // kaydetmenin ortasındaki en güncel hâli okur (kısmi başarıdan sonra yeniden denemede yalnız kalanı yazar).
  const [base, setBase] = useState(group)
  const baseRef = useRef(group)
  function applyBase(next: Group) {
    baseRef.current = next
    setBase(next)
  }
  const [savedMessage, setSavedMessage] = useState(false)
  const text = messages.groups

  const form = useForm<GroupAccessValues>({ defaultValues: groupAccessValues(group) })
  const values = useWatch({ control: form.control }) as GroupAccessValues
  const dirty = form.formState.isDirty

  useEffect(() => {
    if (!dirty && group.version > baseRef.current.version) {
      applyBase(group)
      form.reset(groupAccessValues(group))
    }
  }, [dirty, group, form])

  const version = useRecordVersion({
    recordId: group.id,
    loaded: base.version,
    dirty,
    reload: async () => {
      const list = await queryClient.fetchQuery({
        queryKey: queryKeys.admin.groups(),
        queryFn: () => services.admin.listGroups(),
        staleTime: 0,
      })
      const fresh = list.find((g) => g.id === group.id)
      if (!fresh) return
      applyBase(fresh)
      form.reset(groupAccessValues(fresh))
    },
  })

  const editor = useRecordEditor({
    form,
    // İzin ve kapsam yazmaları aynı grup sürümünü ilerletir: her yanıtın sürümü sonrakine zincirlenir.
    submit: async (submitted) => {
      const changed = changedAccess(submitted, baseRef.current)
      try {
        if (changed.permissions) {
          const saved = await services.admin.setGroupPermissions(
            group.id,
            submitted.permissions,
            version.current(),
          )
          version.advance(saved.version)
          applyBase(saved)
        }
        for (const domain of changed.scopes) {
          const scope = submitted.scopes[domain]
          const saved = await services.admin.setGroupScope(
            group.id,
            domain,
            scope.firms,
            scope.branches,
            version.current(),
          )
          version.advance(saved.version)
          applyBase(saved)
        }
      } finally {
        // Kısmi başarıda da liste güncellensin: kaydedilen parçalar artık "değişmemiş" görünür.
        void queryClient.invalidateQueries({ queryKey: queryKeys.admin.groups() })
      }
    },
    onSaved: () => {
      setSavedMessage(true)
      setTimeout(() => setSavedMessage(false), 2000)
    },
    version,
  })
  // Başka gruba geçiş ya da görünüm değişimi kirli formda onay sorsun (üst sayfa bu korumayı çağırır).
  useEffect(() => {
    registerGuard(editor.requestClose)
    return () => registerGuard(null)
  })

  if (permissionsQuery.isLoading) return <Loading rows={6} />
  if (permissionsQuery.isError)
    return (
      <ErrorState error={permissionsQuery.error} onRetry={() => void permissionsQuery.refetch()} />
    )

  const areas = permissionsByArea(permissionsQuery.data ?? [])

  function togglePermission(key: string) {
    const current = form.getValues('permissions')
    form.setValue(
      'permissions',
      canonicalPermissions(
        current.includes(key) ? current.filter((item) => item !== key) : [...current, key],
      ),
      { shouldDirty: true },
    )
  }

  function setScope(domain: ScopeDomain) {
    return (update: (current: FirmBranchSelection) => FirmBranchSelection) => {
      const scopes = form.getValues('scopes')
      form.setValue(
        'scopes',
        { ...scopes, [domain]: canonicalSelection(update(scopes[domain])) },
        { shouldDirty: true },
      )
    }
  }

  function summary(area: AreaKey): string {
    const count = text.areaPermissions(selectedCount(areas.get(area)!, values.permissions))
    if (area === 'other') return count
    return `${count} · ${text.areaFirms(selectionLabels(values.scopes[area]).length)}`
  }

  return (
    <div className="flex flex-col gap-4">
      {version.alert}
      {version.dialog}
      {editor.dialog}
      {AREA_ORDER.map((area) => (
        <AreaBlock key={area} title={text.areas[area].title} summary={summary(area)}>
          <PermissionChecklist
            permissions={areas.get(area)!}
            selected={values.permissions}
            onToggle={togglePermission}
            byGroup={area === 'other'}
          />
          {area !== 'other' && (
            <FirmScopeEditor
              domain={area}
              selection={values.scopes[area]}
              onChange={setScope(area)}
            />
          )}
        </AreaBlock>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={!editor.dirty || editor.saving}
          onClick={() => void editor.save()}
        >
          {text.save}
        </Button>
        {editor.dirty && !editor.saving && (
          <span className="text-muted-foreground text-sm">{text.unsaved}</span>
        )}
        {savedMessage && <span className="text-positive text-sm">{text.saved}</span>}
      </div>
    </div>
  )
}

export function GroupsPage() {
  const queryClient = useQueryClient()
  const groupsQuery = useQuery({
    queryKey: queryKeys.admin.groups(),
    queryFn: () => services.admin.listGroups(),
  })
  // Görünüm ve seçili grup URL'de: matristen grup başlığına tıklayınca o grubun ekranı açılır.
  const { get, set } = useQueryState()
  const view = get('view') === 'matris' ? 'matris' : 'gruplar'
  const selectedId = get('g') ?? null
  const setSelectedId = (id: string | null) => set({ g: id ?? undefined })
  // Açık grup formu kirliyse grup/görünüm değişimi önce "Kaydedilmemiş değişiklikler" onayı sorar.
  const detailGuard = useRef<LeaveGuard | null>(null)
  const registerGuard = (guard: LeaveGuard | null) => {
    detailGuard.current = guard
  }
  const guarded = (action: () => void) =>
    detailGuard.current ? detailGuard.current(action) : action()
  const permissionsQuery = useQuery({
    queryKey: queryKeys.admin.permissions(),
    queryFn: () => services.admin.listPermissions(),
    enabled: view === 'matris',
  })
  const [createOpen, setCreateOpen] = useState(false)
  const [editingGroup, setEditingGroup] = useState<Group | null>(null)
  const [deletingGroup, setDeletingGroup] = useState<Group | null>(null)

  const deleteMutation = useMutation({
    mutationFn: (groupId: string) => services.admin.deleteGroup(groupId),
    onSuccess: (_result, groupId) => {
      setDeletingGroup(null)
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.groups() })
      if (selectedId === groupId) setSelectedId(null)
    },
  })

  const groups = groupsQuery.data ?? []
  const selected = groups.find((group) => group.id === selectedId) ?? null

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">{messages.groups.title}</h1>
          <p className="text-muted-foreground text-sm">{messages.groups.description}</p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus /> {messages.groups.create}
        </Button>
      </div>

      <ToggleGroup
        variant="outline"
        size="sm"
        className="self-start"
        aria-label={messages.groups.viewLabel}
        value={[view]}
        onValueChange={([value]) => {
          if (value) guarded(() => set({ view: value === 'matris' ? 'matris' : undefined }))
        }}
      >
        <ToggleGroupItem value="gruplar">{messages.groups.viewGroups}</ToggleGroupItem>
        <ToggleGroupItem value="matris">{messages.groups.permissionMatrix}</ToggleGroupItem>
      </ToggleGroup>

      {groupsQuery.isLoading ? (
        <Loading rows={4} />
      ) : groupsQuery.isError ? (
        <ErrorState error={groupsQuery.error} onRetry={() => void groupsQuery.refetch()} />
      ) : groups.length === 0 ? (
        <Empty />
      ) : view === 'matris' ? (
        permissionsQuery.isError ? (
          <ErrorState
            error={permissionsQuery.error}
            onRetry={() => void permissionsQuery.refetch()}
          />
        ) : !permissionsQuery.data ? (
          <Loading rows={6} />
        ) : (
          <GroupPermissionMatrix
            groups={groups}
            permissions={permissionsQuery.data}
            onOpenGroup={(id) => set({ view: undefined, g: id })}
          />
        )
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
          <div className="flex flex-col gap-2">
            {groups.map((group) => (
              <Card
                key={group.id}
                className={cn(
                  'hover:border-primary cursor-pointer p-3 transition-colors',
                  selectedId === group.id && 'border-primary',
                )}
                onClick={() => {
                  if (selectedId !== group.id) guarded(() => setSelectedId(group.id))
                }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{group.name}</span>
                  <div className="flex items-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={messages.common.edit}
                      onClick={(event) => {
                        event.stopPropagation()
                        setEditingGroup(group)
                      }}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={messages.common.delete}
                      onClick={(event) => {
                        event.stopPropagation()
                        setDeletingGroup(group)
                      }}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </div>
                {group.description && (
                  <p className="text-muted-foreground mt-1 text-xs">{group.description}</p>
                )}
                <div className="mt-2 flex flex-wrap gap-1">
                  <Badge variant="secondary">
                    {group.member_count} {messages.groups.memberCount.toLowerCase()}
                  </Badge>
                  {SCOPE_DOMAINS.filter((domain) => !isUnrestricted(group.scopes[domain])).map(
                    (domain) => (
                      <Badge key={domain} variant="outline">
                        {messages.groups.scopeBadge(
                          messages.groups.areas[domain].title,
                          selectionLabels(group.scopes[domain]).join(', '),
                        )}
                      </Badge>
                    ),
                  )}
                </div>
              </Card>
            ))}
          </div>

          <Card className="p-4">
            {selected ? (
              <GroupDetail key={selected.id} group={selected} registerGuard={registerGuard} />
            ) : (
              <Empty title={messages.groups.title} hint={messages.groups.permissionMatrix} />
            )}
          </Card>
        </div>
      )}

      <CreateGroupDialog open={createOpen} onOpenChange={setCreateOpen} />
      <ConfirmDialog
        open={deletingGroup !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingGroup(null)
            deleteMutation.reset()
          }
        }}
        title={messages.groups.deleteConfirm}
        description={messages.groups.deleteConfirmText(deletingGroup?.name ?? '')}
        confirmLabel={messages.common.delete}
        pending={deleteMutation.isPending}
        error={
          deleteMutation.isError
            ? deleteMutation.error instanceof Error
              ? deleteMutation.error.message
              : messages.errors.unexpected
            : null
        }
        onConfirm={() => deletingGroup && deleteMutation.mutate(deletingGroup.id)}
      />
      <EditGroupDialog
        key={editingGroup?.id ?? 'none'}
        group={editingGroup}
        onClose={() => setEditingGroup(null)}
      />
    </div>
  )
}
